<script>
import General from '@/services/General';

export default {
  name: 'modalPreviewHypePopUp',
  props: {
    popupsData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      showModal: false,
      appButtonColor: '#ff6b35',
      appButtonTextColor: '#ffffff',
      isLoadingParams: false,
    };
  },
  methods: {
    async loadAppButtonColors() {
      this.isLoadingParams = true;
      try {
        const colors = await General.getAppButtonColors();
        Object.assign(this, colors);
      } catch (error) {
        console.error('Failed to load app params:', error);
      } finally {
        this.isLoadingParams = false;
      }
    },
    closeModal() {
      this.showModal = false;
    },
    formatActionText(text) {
      if (!text) return 'Buy Now';
      // Map constants to display text
      if (text === 'MARKETING_POPUP_ACTION_WEBSITE') {
        return 'Visit Website';
      }
      if (text === 'MARKETING_POPUP_ACTION_DEEPLINK') {
        return 'Open';
      }
      // If it's a constant with underscores, format it nicely
      if (text.includes('_')) {
        const formatted = text
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
          .replace(/^Marketing Popup Action /i, '')
          .replace(/^Action /i, '');
        return formatted || 'Buy Now';
      }
      return text;
    },
    getImageUrl() {
      return this.popupsData?.background_image || null;
    },
  },
};
</script>

<template>
  <b-modal
    id="preview_hype_popup"
    size="lg"
    v-model="showModal"
    @shown="loadAppButtonColors"
    @hidden="closeModal"
    hide-header
    hide-footer
    body-class="p-0"
    centered
    content-class="preview-modal-content"
  >
    <button class="preview-popup-modal__close" @click="closeModal">
      <i class="uil uil-times"></i>
    </button>
    
    <!-- Loading Overlay -->
    <div v-if="isLoadingParams" class="modal-loader-overlay">
      <div class="modal-loader-content">
        <b-spinner variant="primary" class="mb-2"></b-spinner>
        <p class="mb-0">Loading preview...</p>
      </div>
    </div>
    
    <div class="mobile-phone-preview" v-if="popupsData" :class="{ 'loading-blur': isLoadingParams }">
      <!-- Mobile Phone Frame -->
      <div class="mobile-phone-frame">
        <!-- Phone Screen (dark grey background) -->
        <div class="mobile-phone-screen">
          <!-- Popup Modal -->
          <div class="preview-popup-modal">
            <!-- Decorative Button Image (non-functional) -->
            <div class="preview-popup-modal__decorative-button">
              <i class="uil uil-times"></i>
            </div>
            <!-- Close Button (functional) -->
          

            <!-- Background Image -->
            <div class="preview-popup-modal__background" v-if="getImageUrl()">
              <img :src="getImageUrl()" :alt="popupsData.title || 'Popup image'" />
            </div>

            <!-- Content Overlay -->
            <div class="preview-popup-modal__body">
              <!-- Action Button -->
              <div class="preview-popup-modal__actions" v-if="popupsData.action_text">
                <button 
                  class="preview-popup-modal__button"
                  :style="{
                    backgroundColor: appButtonColor,
                    color: appButtonTextColor
                  }"
                >
                  {{ formatActionText(popupsData.action_text) }}
                </button>
              </div>

              <!-- Additional Link (for participation type) -->
              <div class="preview-popup-modal__link" v-if="popupsData.type === 'participation' && popupsData.participation_tnc_url">
                <a :href="popupsData.participation_tnc_url" target="_blank" class="preview-popup-modal__link-text">
                  Invite your friends for the giveaway!
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<style scoped>
.preview-modal-content {
  border-radius: 0;
  overflow: visible;
  background: transparent;
  border: none;
  box-shadow: none;
}

.mobile-phone-preview {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  background: #f5f5f5;
}

.mobile-phone-frame {
  position: relative;
  width: 325px;
  max-width: 100%;
  background: #5C5D63;
  border-radius: 40px;
  padding: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.mobile-phone-screen {
  width: 100%;
  height: 730ch;
  max-height: 80vh;
  background: #3a3a3a;
  border-radius: 32px;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
}

.preview-popup-modal {
  position: relative;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  max-height: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.preview-popup-modal__decorative-button {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(240, 240, 240, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9;
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.preview-popup-modal__decorative-button i {
  font-size: 24px;
  color: #ffffff;
  font-weight: 600;
  opacity: 0.5;
}

.preview-popup-modal__close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #5C5D63;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 11;
  transition: opacity 0.2s ease;
}

.preview-popup-modal__close:hover {
  opacity: 0.8;
}

.preview-popup-modal__close i {
  font-size: 24px;
  color: #ffffff;
  font-weight: 600;
}

.preview-popup-modal__background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  overflow: hidden;
}

.preview-popup-modal__background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-popup-modal__body {
  position: relative;
  z-index: 2;
  padding: 24px 20px;
  flex: 1;
  overflow-y: auto;
  text-align: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 100%;
}

.preview-popup-modal__title {
  font-size: 22px;
  font-weight: 700;
  color: #000000;
  margin: 0 0 16px 0;
  line-height: 1.3;
  text-align: center;
}

.preview-popup-modal__top-text {
  color: #000000;
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 12px;
  font-weight: 400;
  text-align: center;
}

.preview-popup-modal__top-text p {
  margin: 0 0 8px 0;
}

.preview-popup-modal__top-text p:last-child {
  margin-bottom: 0;
}

.preview-popup-modal__text {
  color: #000000;
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 20px;
  font-weight: 400;
  text-align: center;
}

.preview-popup-modal__text p {
  margin: 0 0 12px 0;
}

.preview-popup-modal__text p:last-child {
  margin-bottom: 0;
}

.preview-popup-modal__actions {
  margin-bottom: 12px;
}

.preview-popup-modal__button {
  width: 100%;
  padding: 10px 24px;
  background: #ff6b35; /* Default fallback */
  color: #ffffff; /* Default fallback */
  border: none;
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif !important;
  font-size: 16px;
  font-weight: 700 !important;
  font-style: normal !important;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.2s ease;
  letter-spacing: 0.5px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: optimizeLegibility;
}

.preview-popup-modal__button:hover {
  opacity: 0.9;
  filter: brightness(0.9);
}

.preview-popup-modal__link {
  text-align: center;
  margin-top: 4px;
}

.preview-popup-modal__link-text {
  color: #ff6b35;
  text-decoration: underline;
  font-size: 14px;
  cursor: pointer;
  transition: color 0.2s ease;
  font-weight: 400;
}

.preview-popup-modal__link-text:hover {
  color: #e55a2b;
}

@media (max-width: 768px) {
  .mobile-phone-frame {
    width: 100%;
    max-width: 325px;
    padding: 8px;
  }

  .mobile-phone-screen {
    height: 600px;
    padding: 15px;
  }

  .preview-popup-modal__body {
    padding: 20px 16px;
  }

  .preview-popup-modal__title {
    font-size: 20px;
  }

  .preview-popup-modal__text {
    font-size: 14px;
  }
}

.modal-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  border-radius: 8px;
}

.modal-loader-content {
  text-align: center;
  color: #5f6b7d;
  font-size: 16px;
}

.loading-blur {
  filter: blur(2px);
  opacity: 0.7;
  pointer-events: none;
}

@media (max-width: 576px) {
  .mobile-phone-preview {
    padding: 10px;
  }

  .mobile-phone-frame {
    border-radius: 30px;
    padding: 6px;
  }

  .mobile-phone-screen {
    border-radius: 24px;
    height: 500px;
    padding: 10px;
  }

  .preview-popup-modal {
    border-radius: 16px;
  }

  .preview-popup-modal__body {
    padding: 18px 14px;
  }

  .preview-popup-modal__title {
    font-size: 18px;
    margin-bottom: 12px;
  }

  .preview-popup-modal__text {
    font-size: 13px;
    margin-bottom: 16px;
  }

  .preview-popup-modal__button {
    padding: 14px 20px;
    font-size: 14px;
  }
}

.modal-loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  border-radius: 8px;
}

.modal-loader-content {
  text-align: center;
  color: #5f6b7d;
  font-size: 16px;
}

.loading-blur {
  filter: blur(2px);
  opacity: 0.7;
  pointer-events: none;
}
</style>
